module.exports = {
	"data": {
		"markdownRemark": {
			"html": "<h3>Creating Users</h3>\n<p>So far you’ve been working only with the <code>Link</code> type, but it’s time to include <code>User</code> as well so that the app can show who posted a link and who voted on it.</p>\n<p>You’ll need some registered users for this, so start by implementing the mutation for creating them.</p>\n<Instruction>\n<p>You already know the process for this, but let’s go through each step again.</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"   nocopy=\"false\"><code>bundle <span class=\"token function\">exec</span> rails generate model User name email password_digest\nbundle <span class=\"token function\">exec</span> rails db:migrate\n</code></pre>\n      </div>\n</Instruction>\n<p>This generates a <code>user.rb</code> file in <code>app/models</code>.</p>\n<Instruction>\n<p>Update the user model contents to:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/models/user.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">class</span> <span class=\"token class-name\">User</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">ApplicationRecord</span>\n  has_secure_password\n\n  validates <span class=\"token symbol\">:name</span><span class=\"token punctuation\">,</span> presence<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n  validates <span class=\"token symbol\">:email</span><span class=\"token punctuation\">,</span> presence<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span><span class=\"token punctuation\">,</span> uniqueness<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Now we have users, which are required to have <code>name</code> and <code>email</code>.</p>\n<p>They also have a <a href=\"http://api.rubyonrails.org/classes/ActiveModel/SecurePassword/ClassMethods.html#method-i-has_secure_password\">secure password</a>. But in order for <a href=\"http://api.rubyonrails.org/classes/ActiveModel/SecurePassword/ClassMethods.html#method-i-has_secure_password\">has_secure_password</a>, <a href=\"https://rubygems.org/gems/bcrypt\">bcrypt</a> gem. It used encrypting and verifying user passwords.</p>\n<Instruction>\n<p>Add <code>Gemfile</code> the following line:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/Gemfile\"  nocopy=\"false\"><code>gem <span class=\"token string\">'bcrypt'</span><span class=\"token punctuation\">,</span> <span class=\"token string\">'~> 3.1.7'</span>\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>Then run:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"   nocopy=\"false\"><code>bundle update\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>Now, lets create GraphQL type for representing a user:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/graphql/types/user_type.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">module</span> <span class=\"token constant\">Types</span>\n  <span class=\"token keyword\">class</span> <span class=\"token class-name\">UserType</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">BaseObject</span>\n    field <span class=\"token symbol\">:id</span><span class=\"token punctuation\">,</span> <span class=\"token constant\">ID</span><span class=\"token punctuation\">,</span> null<span class=\"token punctuation\">:</span> <span class=\"token keyword\">false</span>\n    field <span class=\"token symbol\">:name</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> null<span class=\"token punctuation\">:</span> <span class=\"token keyword\">false</span>\n    <span class=\"token comment\" spellcheck=\"true\"># we are exposing `email` just for tutorial purposes</span>\n    <span class=\"token comment\" spellcheck=\"true\"># in real application shouldn't leak user emails</span>\n    field <span class=\"token symbol\">:email</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> null<span class=\"token punctuation\">:</span> <span class=\"token keyword\">false</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Now when we have our user model and its GraphQL type. We need a way to create users. Users would be created by <code>name</code>, <code>email</code> and <code>password</code>.</p>\n<Instruction>\n<p>Create a type for the authentication provider <a href=\"http://graphql-ruby.org/type_definitions/input_objects.html\">input type</a>:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/graphql/types/auth_provider_email_input.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">module</span> <span class=\"token constant\">Types</span>\n  <span class=\"token keyword\">class</span> <span class=\"token class-name\">AuthProviderEmailInput</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">BaseInputObject</span>\n    <span class=\"token comment\" spellcheck=\"true\"># the name is usually inferred by class name but can be overwritten</span>\n    graphql_name <span class=\"token string\">'AUTH_PROVIDER_EMAIL'</span>\n\n    argument <span class=\"token symbol\">:email</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> required<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n    argument <span class=\"token symbol\">:password</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> required<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>Then create a mutation for creating a user:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/graphql/mutations/create_user.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">module</span> <span class=\"token constant\">Mutations</span>\n  <span class=\"token keyword\">class</span> <span class=\"token class-name\">CreateUser</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">BaseMutation</span>\n    <span class=\"token comment\" spellcheck=\"true\"># often we will need input types for specific mutation</span>\n    <span class=\"token comment\" spellcheck=\"true\"># in those cases we can define those input types in the mutation class itself</span>\n    <span class=\"token keyword\">class</span> <span class=\"token class-name\">AuthProviderSignupData</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">Types</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:BaseInputObject</span>\n      argument <span class=\"token symbol\">:email</span><span class=\"token punctuation\">,</span> <span class=\"token constant\">Types</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:AuthProviderEmailInput</span><span class=\"token punctuation\">,</span> required<span class=\"token punctuation\">:</span> <span class=\"token keyword\">false</span>\n    <span class=\"token keyword\">end</span>\n\n    argument <span class=\"token symbol\">:name</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> required<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n    argument <span class=\"token symbol\">:auth_provider</span><span class=\"token punctuation\">,</span> <span class=\"token constant\">AuthProviderSignupData</span><span class=\"token punctuation\">,</span> required<span class=\"token punctuation\">:</span> <span class=\"token keyword\">false</span>\n\n    type <span class=\"token constant\">Types</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:UserType</span>\n\n    <span class=\"token keyword\">def</span> <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>name<span class=\"token punctuation\">:</span> <span class=\"token keyword\">nil</span><span class=\"token punctuation\">,</span> auth_provider<span class=\"token punctuation\">:</span> <span class=\"token keyword\">nil</span><span class=\"token punctuation\">)</span>\n      <span class=\"token constant\">User</span><span class=\"token punctuation\">.</span>create<span class=\"token operator\">!</span><span class=\"token punctuation\">(</span>\n        name<span class=\"token punctuation\">:</span> name<span class=\"token punctuation\">,</span>\n        email<span class=\"token punctuation\">:</span> auth_provider<span class=\"token operator\">&amp;</span><span class=\"token punctuation\">.</span><span class=\"token punctuation\">[</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token symbol\">:email</span><span class=\"token punctuation\">)</span><span class=\"token operator\">&amp;</span><span class=\"token punctuation\">.</span><span class=\"token punctuation\">[</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token symbol\">:email</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n        password<span class=\"token punctuation\">:</span> auth_provider<span class=\"token operator\">&amp;</span><span class=\"token punctuation\">.</span><span class=\"token punctuation\">[</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token symbol\">:email</span><span class=\"token punctuation\">)</span><span class=\"token operator\">&amp;</span><span class=\"token punctuation\">.</span><span class=\"token punctuation\">[</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token symbol\">:password</span><span class=\"token punctuation\">)</span>\n      <span class=\"token punctuation\">)</span>\n    <span class=\"token keyword\">end</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>And add it to mutations list:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/graphql/types/mutation_type.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">module</span> <span class=\"token constant\">Types</span>\n  <span class=\"token keyword\">class</span> <span class=\"token class-name\">MutationType</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">BaseObject</span>\n    field <span class=\"token symbol\">:create_user</span><span class=\"token punctuation\">,</span> mutation<span class=\"token punctuation\">:</span> <span class=\"token constant\">Mutations</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:CreateUser</span>\n    field <span class=\"token symbol\">:create_link</span><span class=\"token punctuation\">,</span> mutation<span class=\"token punctuation\">:</span> <span class=\"token constant\">Mutations</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:CreateLink</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Now, you can create a new user using <a href=\"https://github.com/graphql/graphiql\">GraphiQL</a>:</p>\n<p><img src=\"http://i.imgur.com/SVg4T6z.png\"></p>\n<Instruction>\n<p>Here is how the unit test for your mutation is going to look like:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/test/graphql/mutations/create_user_test.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">require</span> <span class=\"token string\">'test_helper'</span>\n\n<span class=\"token keyword\">class</span> <span class=\"token class-name\">Mutations</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:CreateUserTest</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">ActiveSupport</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:TestCase</span>\n  <span class=\"token keyword\">def</span> <span class=\"token function\">perform</span><span class=\"token punctuation\">(</span>args <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span><span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span>\n    <span class=\"token constant\">Mutations</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:CreateUser</span><span class=\"token punctuation\">.</span><span class=\"token keyword\">new</span><span class=\"token punctuation\">(</span>object<span class=\"token punctuation\">:</span> <span class=\"token keyword\">nil</span><span class=\"token punctuation\">,</span> context<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span><span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span><span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>args<span class=\"token punctuation\">)</span>\n  <span class=\"token keyword\">end</span>\n\n  test <span class=\"token string\">'create new user'</span> <span class=\"token keyword\">do</span>\n    user <span class=\"token operator\">=</span> <span class=\"token function\">perform</span><span class=\"token punctuation\">(</span>\n      name<span class=\"token punctuation\">:</span> <span class=\"token string\">'Test User'</span><span class=\"token punctuation\">,</span>\n      auth_provider<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>\n        email<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>\n          email<span class=\"token punctuation\">:</span> <span class=\"token string\">'email@example.com'</span><span class=\"token punctuation\">,</span>\n          password<span class=\"token punctuation\">:</span> <span class=\"token string\">'[omitted]'</span>\n        <span class=\"token punctuation\">}</span>\n      <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">)</span>\n\n    assert user<span class=\"token punctuation\">.</span>persisted<span class=\"token operator\">?</span>\n    assert_equal user<span class=\"token punctuation\">.</span>name<span class=\"token punctuation\">,</span> <span class=\"token string\">'Test User'</span>\n    assert_equal user<span class=\"token punctuation\">.</span>email<span class=\"token punctuation\">,</span> <span class=\"token string\">'email@example.com'</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n<p>You can run the tests with the following command:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"   nocopy=\"false\"><code>bundle <span class=\"token function\">exec</span> rails <span class=\"token function\">test</span>\n</code></pre>\n      </div>\n</Instruction>\n<h3>Sign in Mutation</h3>\n<p>Now that you have users, how would you sign them in using GraphQL? With a new mutation, of course! Mutations are a way for the client to talk to the server whenever it needs an operation that isn’t just about fetching data.</p>\n<p>For this first time signing users in through GraphQL you’ll be using a simple email/password login method, returning a token that can be used in subsequent requests for authentication.</p>\n<blockquote>\n<p>Note that this is <strong>NOT</strong> supposed to be a production-ready authentication feature, but just a small functioning prototype to show the basic concept. In a real app, you should make sure to encrypt passwords properly before passing them around and use a good token generation method, such as <a href=\"https://jwt.io/\">JWT</a>.</p>\n</blockquote>\n<p>Again, the workflow for adding this mutation will be very similar to the ones we’ve done before:</p>\n<Instruction>\n<p>Create a resolver for the mutation:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/graphql/mutations/sign_in_user.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">module</span> <span class=\"token constant\">Mutations</span>\n  <span class=\"token keyword\">class</span> <span class=\"token class-name\">SignInUser</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">BaseMutation</span>\n    null <span class=\"token keyword\">true</span>\n\n    argument <span class=\"token symbol\">:email</span><span class=\"token punctuation\">,</span> <span class=\"token constant\">Types</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:AuthProviderEmailInput</span><span class=\"token punctuation\">,</span> required<span class=\"token punctuation\">:</span> <span class=\"token keyword\">false</span>\n\n    field <span class=\"token symbol\">:token</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> null<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n    field <span class=\"token symbol\">:user</span><span class=\"token punctuation\">,</span> <span class=\"token constant\">Types</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:UserType</span><span class=\"token punctuation\">,</span> null<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n\n    <span class=\"token keyword\">def</span> <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>email<span class=\"token punctuation\">:</span> <span class=\"token keyword\">nil</span><span class=\"token punctuation\">)</span>\n      <span class=\"token comment\" spellcheck=\"true\"># basic validation</span>\n      <span class=\"token keyword\">return</span> <span class=\"token keyword\">unless</span> email\n\n      user <span class=\"token operator\">=</span> <span class=\"token constant\">User</span><span class=\"token punctuation\">.</span>find_by email<span class=\"token punctuation\">:</span> email<span class=\"token punctuation\">[</span><span class=\"token symbol\">:email</span><span class=\"token punctuation\">]</span>\n\n      <span class=\"token comment\" spellcheck=\"true\"># ensures we have the correct user</span>\n      <span class=\"token keyword\">return</span> <span class=\"token keyword\">unless</span> user\n      <span class=\"token keyword\">return</span> <span class=\"token keyword\">unless</span> user<span class=\"token punctuation\">.</span><span class=\"token function\">authenticate</span><span class=\"token punctuation\">(</span>email<span class=\"token punctuation\">[</span><span class=\"token symbol\">:password</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">)</span>\n\n      <span class=\"token comment\" spellcheck=\"true\"># use Ruby on Rails - ActiveSupport::MessageEncryptor, to build a token</span>\n      crypt <span class=\"token operator\">=</span> <span class=\"token constant\">ActiveSupport</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:MessageEncryptor</span><span class=\"token punctuation\">.</span><span class=\"token keyword\">new</span><span class=\"token punctuation\">(</span><span class=\"token constant\">Rails</span><span class=\"token punctuation\">.</span>application<span class=\"token punctuation\">.</span>credentials<span class=\"token punctuation\">.</span>secret_key_base<span class=\"token punctuation\">.</span><span class=\"token function\">byteslice</span><span class=\"token punctuation\">(</span><span class=\"token number\">0</span><span class=\"token punctuation\">.</span><span class=\"token punctuation\">.</span><span class=\"token number\">31</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n      token <span class=\"token operator\">=</span> crypt<span class=\"token punctuation\">.</span><span class=\"token function\">encrypt_and_sign</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"user-id:<span class=\"token interpolation\"><span class=\"token delimiter tag\">#{</span> user<span class=\"token punctuation\">.</span>id <span class=\"token delimiter tag\">}</span></span>\"</span><span class=\"token punctuation\">)</span>\n\n      <span class=\"token punctuation\">{</span> user<span class=\"token punctuation\">:</span> user<span class=\"token punctuation\">,</span> token<span class=\"token punctuation\">:</span> token <span class=\"token punctuation\">}</span>\n    <span class=\"token keyword\">end</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>Add the resolver for the mutation to the mutations list:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/graphql/types/mutation_type.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">module</span> <span class=\"token constant\">Types</span>\n  <span class=\"token keyword\">class</span> <span class=\"token class-name\">MutationType</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">BaseObject</span>\n    field <span class=\"token symbol\">:create_user</span><span class=\"token punctuation\">,</span> mutation<span class=\"token punctuation\">:</span> <span class=\"token constant\">Mutations</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:CreateUser</span>\n    field <span class=\"token symbol\">:create_link</span><span class=\"token punctuation\">,</span> mutation<span class=\"token punctuation\">:</span> <span class=\"token constant\">Mutations</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:CreateLink</span>\n    field <span class=\"token symbol\">:signin_user</span><span class=\"token punctuation\">,</span> mutation<span class=\"token punctuation\">:</span> <span class=\"token constant\">Mutations</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:SignInUser</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Now, you can get the token by using <a href=\"https://github.com/graphql/graphiql\">GraphiQL</a>:</p>\n<p><img src=\"http://i.imgur.com/jmMofgT.png\"></p>\n<Instruction>\n<p>Here is how the unit test for your mutation is going to look like:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/test/graphql/mutations/sign_in_user_test.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">require</span> <span class=\"token string\">'test_helper'</span>\n\n<span class=\"token keyword\">class</span> <span class=\"token class-name\">Mutations</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:SignInUserTest</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">ActiveSupport</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:TestCase</span>\n  <span class=\"token keyword\">def</span> <span class=\"token function\">perform</span><span class=\"token punctuation\">(</span>args <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span><span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span>\n    <span class=\"token constant\">Mutations</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:SignInUser</span><span class=\"token punctuation\">.</span><span class=\"token keyword\">new</span><span class=\"token punctuation\">(</span>object<span class=\"token punctuation\">:</span> <span class=\"token keyword\">nil</span><span class=\"token punctuation\">,</span> context<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> session<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span><span class=\"token punctuation\">}</span> <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span><span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>args<span class=\"token punctuation\">)</span>\n  <span class=\"token keyword\">end</span>\n\n  <span class=\"token keyword\">def</span> create_user\n    <span class=\"token constant\">User</span><span class=\"token punctuation\">.</span>create<span class=\"token operator\">!</span><span class=\"token punctuation\">(</span>\n      name<span class=\"token punctuation\">:</span> <span class=\"token string\">'Test User'</span><span class=\"token punctuation\">,</span>\n      email<span class=\"token punctuation\">:</span> <span class=\"token string\">'email@example.com'</span><span class=\"token punctuation\">,</span>\n      password<span class=\"token punctuation\">:</span> <span class=\"token string\">'[omitted]'</span><span class=\"token punctuation\">,</span>\n    <span class=\"token punctuation\">)</span>\n  <span class=\"token keyword\">end</span>\n\n  test <span class=\"token string\">'success'</span> <span class=\"token keyword\">do</span>\n    user <span class=\"token operator\">=</span> create_user\n\n    result <span class=\"token operator\">=</span> <span class=\"token function\">perform</span><span class=\"token punctuation\">(</span>\n      email<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>\n        email<span class=\"token punctuation\">:</span> user<span class=\"token punctuation\">.</span>email<span class=\"token punctuation\">,</span>\n        password<span class=\"token punctuation\">:</span> user<span class=\"token punctuation\">.</span>password\n      <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">)</span>\n\n    assert result<span class=\"token punctuation\">[</span><span class=\"token symbol\">:token</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">.</span>present<span class=\"token operator\">?</span>\n    assert_equal result<span class=\"token punctuation\">[</span><span class=\"token symbol\">:user</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">,</span> user\n  <span class=\"token keyword\">end</span>\n\n  test <span class=\"token string\">'failure because no credentials'</span> <span class=\"token keyword\">do</span>\n    assert_nil perform\n  <span class=\"token keyword\">end</span>\n\n  test <span class=\"token string\">'failure because wrong email'</span> <span class=\"token keyword\">do</span>\n    create_user\n    assert_nil <span class=\"token function\">perform</span><span class=\"token punctuation\">(</span>email<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> email<span class=\"token punctuation\">:</span> <span class=\"token string\">'wrong'</span> <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span>\n  <span class=\"token keyword\">end</span>\n\n  test <span class=\"token string\">'failure because wrong password'</span> <span class=\"token keyword\">do</span>\n    user <span class=\"token operator\">=</span> create_user\n    assert_nil <span class=\"token function\">perform</span><span class=\"token punctuation\">(</span>email<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> email<span class=\"token punctuation\">:</span> user<span class=\"token punctuation\">.</span>email<span class=\"token punctuation\">,</span> password<span class=\"token punctuation\">:</span> <span class=\"token string\">'wrong'</span> <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n<p>You can run the tests with the following command:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"   nocopy=\"false\"><code>bundle <span class=\"token function\">exec</span> rails <span class=\"token function\">test</span>\n</code></pre>\n      </div>\n</Instruction>\n<h3>Authenticating requests</h3>\n<p>With the token that the <code>signinUser</code> mutation provides, apps can authenticate subsequent requests. There are a couple of ways this can be done. In this tutorial, we are just going to use the built-in <strong>session</strong>, since this doesn’t add any requirements to the client application. The GraphQL server should be able to get the token from the session header on each request, detect what user it relates to, and pass this information down to the mutations.</p>\n<p>The best place to put data shared between mutations is in the context object.\nYou’ll need that object to be different in every request now though, since each one may be from a different user.</p>\n<Instruction>\n<p>Thankfully, the <a href=\"https://rubygems.org/gems/graphql\">graphql gem</a> allows that you’ll just have to change the auto-generated <code>graphql_controller.rb</code> to accomplish this:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/controllers/graphql_controller.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">class</span> <span class=\"token class-name\">GraphqlController</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">ApplicationController</span>\n  <span class=\"token keyword\">def</span> execute\n    variables <span class=\"token operator\">=</span> <span class=\"token function\">ensure_hash</span><span class=\"token punctuation\">(</span>params<span class=\"token punctuation\">[</span><span class=\"token symbol\">:variables</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">)</span>\n    query <span class=\"token operator\">=</span> params<span class=\"token punctuation\">[</span><span class=\"token symbol\">:query</span><span class=\"token punctuation\">]</span>\n    operation_name <span class=\"token operator\">=</span> params<span class=\"token punctuation\">[</span><span class=\"token symbol\">:operationName</span><span class=\"token punctuation\">]</span>\n    context <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span>\n      <span class=\"token comment\" spellcheck=\"true\"># we need to provide session and current user</span>\n      session<span class=\"token punctuation\">:</span> session<span class=\"token punctuation\">,</span>\n      current_user<span class=\"token punctuation\">:</span> current_user\n    <span class=\"token punctuation\">}</span>\n    result <span class=\"token operator\">=</span> <span class=\"token constant\">GraphqlTutorialSchema</span><span class=\"token punctuation\">.</span><span class=\"token function\">execute</span><span class=\"token punctuation\">(</span>query<span class=\"token punctuation\">,</span> variables<span class=\"token punctuation\">:</span> variables<span class=\"token punctuation\">,</span> context<span class=\"token punctuation\">:</span> context<span class=\"token punctuation\">,</span> operation_name<span class=\"token punctuation\">:</span> operation_name<span class=\"token punctuation\">)</span>\n    render json<span class=\"token punctuation\">:</span> result\n  <span class=\"token keyword\">rescue</span> <span class=\"token operator\">=</span><span class=\"token operator\">></span> e\n    <span class=\"token keyword\">raise</span> e <span class=\"token keyword\">unless</span> <span class=\"token constant\">Rails</span><span class=\"token punctuation\">.</span>env<span class=\"token punctuation\">.</span>development<span class=\"token operator\">?</span>\n    handle_error_in_development e\n  <span class=\"token keyword\">end</span>\n\n  private\n\n  <span class=\"token comment\" spellcheck=\"true\"># gets current user from token stored in the session</span>\n  <span class=\"token keyword\">def</span> current_user\n    <span class=\"token comment\" spellcheck=\"true\"># if we want to change the sign-in strategy, this is the place to do it</span>\n    <span class=\"token keyword\">return</span> <span class=\"token keyword\">unless</span> session<span class=\"token punctuation\">[</span><span class=\"token symbol\">:token</span><span class=\"token punctuation\">]</span>\n\n    crypt <span class=\"token operator\">=</span> <span class=\"token constant\">ActiveSupport</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:MessageEncryptor</span><span class=\"token punctuation\">.</span><span class=\"token keyword\">new</span><span class=\"token punctuation\">(</span><span class=\"token constant\">Rails</span><span class=\"token punctuation\">.</span>application<span class=\"token punctuation\">.</span>credentials<span class=\"token punctuation\">.</span>secret_key_base<span class=\"token punctuation\">.</span><span class=\"token function\">byteslice</span><span class=\"token punctuation\">(</span><span class=\"token number\">0</span><span class=\"token punctuation\">.</span><span class=\"token punctuation\">.</span><span class=\"token number\">31</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n    token <span class=\"token operator\">=</span> crypt<span class=\"token punctuation\">.</span>decrypt_and_verify session<span class=\"token punctuation\">[</span><span class=\"token symbol\">:token</span><span class=\"token punctuation\">]</span>\n    user_id <span class=\"token operator\">=</span> token<span class=\"token punctuation\">.</span><span class=\"token function\">gsub</span><span class=\"token punctuation\">(</span><span class=\"token string\">'user-id:'</span><span class=\"token punctuation\">,</span> <span class=\"token string\">''</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span>to_i\n    <span class=\"token constant\">User</span><span class=\"token punctuation\">.</span>find_by id<span class=\"token punctuation\">:</span> user_id\n  <span class=\"token keyword\">rescue</span> <span class=\"token constant\">ActiveSupport</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:MessageVerifier</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:InvalidSignature</span>\n    <span class=\"token keyword\">nil</span>\n  <span class=\"token keyword\">end</span>\n\n  <span class=\"token comment\" spellcheck=\"true\"># Handle form data, JSON body, or a blank value</span>\n  <span class=\"token keyword\">def</span> <span class=\"token function\">ensure_hash</span><span class=\"token punctuation\">(</span>ambiguous_param<span class=\"token punctuation\">)</span>\n    <span class=\"token comment\" spellcheck=\"true\"># ...code</span>\n  <span class=\"token keyword\">end</span>\n\n  <span class=\"token keyword\">def</span> <span class=\"token function\">handle_error_in_development</span><span class=\"token punctuation\">(</span>e<span class=\"token punctuation\">)</span>\n    <span class=\"token comment\" spellcheck=\"true\"># ...code</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>We also need to update our <code>signinUser</code> resolver, so it also stores the <code>token</code> in <code>session</code>. Be sure to remove the <code>_</code> since the variable is now in use:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/graphql/mutations/sign_in_user.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">module</span> <span class=\"token constant\">Mutations</span>\n  <span class=\"token keyword\">class</span> <span class=\"token class-name\">SignInUser</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">BaseMutation</span>\n    <span class=\"token comment\" spellcheck=\"true\"># ...code</span>\n\n    <span class=\"token keyword\">def</span> <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>email<span class=\"token punctuation\">:</span> <span class=\"token keyword\">nil</span><span class=\"token punctuation\">)</span>\n      <span class=\"token comment\" spellcheck=\"true\"># ...code</span>\n\n      crypt <span class=\"token operator\">=</span> <span class=\"token constant\">ActiveSupport</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:MessageEncryptor</span><span class=\"token punctuation\">.</span><span class=\"token keyword\">new</span><span class=\"token punctuation\">(</span><span class=\"token constant\">Rails</span><span class=\"token punctuation\">.</span>application<span class=\"token punctuation\">.</span>credentials<span class=\"token punctuation\">.</span>secret_key_base<span class=\"token punctuation\">.</span><span class=\"token function\">byteslice</span><span class=\"token punctuation\">(</span><span class=\"token number\">0</span><span class=\"token punctuation\">.</span><span class=\"token punctuation\">.</span><span class=\"token number\">31</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n      token <span class=\"token operator\">=</span> crypt<span class=\"token punctuation\">.</span><span class=\"token function\">encrypt_and_sign</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"user-id:<span class=\"token interpolation\"><span class=\"token delimiter tag\">#{</span> user<span class=\"token punctuation\">.</span>id <span class=\"token delimiter tag\">}</span></span>\"</span><span class=\"token punctuation\">)</span>\n\n      context<span class=\"token punctuation\">[</span><span class=\"token symbol\">:session</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">[</span><span class=\"token symbol\">:token</span><span class=\"token punctuation\">]</span> <span class=\"token operator\">=</span> token\n\n      <span class=\"token comment\" spellcheck=\"true\"># ...code</span>\n    <span class=\"token keyword\">end</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>This is pretty straightforward since the generated token is so simple. Like was said before, make sure to check out a different token method out there when building a real-world application though, such as <a href=\"https://jwt.io/\">JWT</a>.</p>\n<h3>Linking User to Created Links</h3>\n<Instruction>\n<p>Your server can now detect the user that triggered each GraphQL request. This could be useful in many situations. For example, the authenticated user should be exactly the one that posted a link being created with the <code>createLink</code> mutation. You can now store this information for each link.</p>\n<p>First run the following script:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"   nocopy=\"false\"><code>bundle <span class=\"token function\">exec</span> rails generate migration add_user_id_link\n</code></pre>\n      </div>\n<p>It generates a <a href=\"http://edgeguides.rubyonrails.org/active_record_migrations.html\">database migration</a>.</p>\n</Instruction>\n<Instruction>\n<p>Change its contents to:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"   nocopy=\"false\"><code><span class=\"token keyword\">class</span> <span class=\"token class-name\">AddUserIdLink</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">ActiveRecord</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:Migration</span><span class=\"token punctuation\">[</span><span class=\"token number\">5.1</span><span class=\"token punctuation\">]</span>\n  <span class=\"token keyword\">def</span> change\n    change_table <span class=\"token symbol\">:links</span> <span class=\"token keyword\">do</span> <span class=\"token operator\">|</span>t<span class=\"token operator\">|</span>\n      t<span class=\"token punctuation\">.</span>references <span class=\"token symbol\">:user</span><span class=\"token punctuation\">,</span> foreign_key<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n    <span class=\"token keyword\">end</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n<p>Then run the following script:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"   nocopy=\"false\"><code>bundle <span class=\"token function\">exec</span> rails db:migrate\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>This connects the  <code>users</code> and <code>links</code> tables in the database. Then you need to update the <code>Link</code> model:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/models/link.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">class</span> <span class=\"token class-name\">Link</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">ApplicationRecord</span>\n  belongs_to <span class=\"token symbol\">:user</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>Also you have to update the <code>LinkType</code>:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/graphql/types/link_type.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">module</span> <span class=\"token constant\">Types</span>\n  <span class=\"token keyword\">class</span> <span class=\"token class-name\">LinkType</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">BaseObject</span>\n    field <span class=\"token symbol\">:id</span><span class=\"token punctuation\">,</span> <span class=\"token constant\">ID</span><span class=\"token punctuation\">,</span> null<span class=\"token punctuation\">:</span> <span class=\"token keyword\">false</span>\n    field <span class=\"token symbol\">:url</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> null<span class=\"token punctuation\">:</span> <span class=\"token keyword\">false</span>\n    field <span class=\"token symbol\">:description</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> null<span class=\"token punctuation\">:</span> <span class=\"token keyword\">false</span>\n    <span class=\"token comment\" spellcheck=\"true\"># `posted_by` is automatically camelcased as `postedBy`</span>\n    <span class=\"token comment\" spellcheck=\"true\"># field can be nil, because we added users relationship later</span>\n    <span class=\"token comment\" spellcheck=\"true\"># \"method\" option remaps field to an attribute of Link model</span>\n    field <span class=\"token symbol\">:posted_by</span><span class=\"token punctuation\">,</span> <span class=\"token constant\">UserType</span><span class=\"token punctuation\">,</span> null<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span><span class=\"token punctuation\">,</span> method<span class=\"token punctuation\">:</span> <span class=\"token symbol\">:user</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>And update <code>CreateLink</code> resolver:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-ruby\"  path=\".../graphql-ruby/app/graphql/mutations/create_link.rb\"  nocopy=\"false\"><code><span class=\"token keyword\">module</span> <span class=\"token constant\">Mutations</span>\n  <span class=\"token keyword\">class</span> <span class=\"token class-name\">CreateLink</span> <span class=\"token operator\">&lt;</span> <span class=\"token constant\">BaseMutation</span>\n    argument <span class=\"token symbol\">:description</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> required<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n    argument <span class=\"token symbol\">:url</span><span class=\"token punctuation\">,</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> required<span class=\"token punctuation\">:</span> <span class=\"token keyword\">true</span>\n\n    type <span class=\"token constant\">Types</span><span class=\"token punctuation\">:</span><span class=\"token symbol\">:LinkType</span>\n\n    <span class=\"token keyword\">def</span> <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>description<span class=\"token punctuation\">:</span> <span class=\"token keyword\">nil</span><span class=\"token punctuation\">,</span> url<span class=\"token punctuation\">:</span> <span class=\"token keyword\">nil</span><span class=\"token punctuation\">)</span>\n      <span class=\"token constant\">Link</span><span class=\"token punctuation\">.</span>create<span class=\"token operator\">!</span><span class=\"token punctuation\">(</span>\n        description<span class=\"token punctuation\">:</span> description<span class=\"token punctuation\">,</span>\n        url<span class=\"token punctuation\">:</span> url<span class=\"token punctuation\">,</span>\n        user<span class=\"token punctuation\">:</span> context<span class=\"token punctuation\">[</span><span class=\"token symbol\">:current_user</span><span class=\"token punctuation\">]</span>\n      <span class=\"token punctuation\">)</span>\n    <span class=\"token keyword\">end</span>\n  <span class=\"token keyword\">end</span>\n<span class=\"token keyword\">end</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Done! Now when you post links, they will be attached to your user, so you have to run  <code>signinUser</code> beforehand.</p>\n<p><img src=\"http://i.imgur.com/9ma8r8u.png\"></p>",
			"fields": {
				"slug": "/graphql-ruby/4-authentication/"
			},
			"excerpt": "Creating Users So far you’ve been working only with the   type, but it’s time to include   as well so that the app can show who posted a...",
			"frontmatter": {
				"title": "Authentication",
				"videoId": null,
				"videoAuthor": null,
				"parent": "/opt/build/repo/content/backend/graphql-ruby/4-authentication.md absPath of file",
				"question": null,
				"answers": null,
				"correctAnswer": null,
				"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Ruby.",
				"pageTitle": "Server-side Authentication with GraphQL & Ruby Tutorial"
			}
		},
		"mds": {
			"edges": [
				{
					"node": {
						"frontmatter": {
							"title": "Get started with your favorite technology",
							"description": null
						},
						"fields": {
							"slug": "/choose/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "adding users",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/1-adding-users/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "adding new Snap settings to code",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/0-adding-new-Snap-settings-to-code/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "aws",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/4-aws/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "apache",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/2-apache/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "custom css themes",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/10-custom-css-themes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "It was great having you!",
							"description": null
						},
						"fields": {
							"slug": "/success/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "archive and delete",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/3-archive-and-delete/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "azure",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/5-azure/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "backup",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/6-backup/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "caddy webserver config",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/7-caddy-webserver-config/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "cloud foundry",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/8-cloud-foundry/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "cloudron",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/9-cloudron/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-elixir/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Elixir & Absinthe as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-elixir/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Absinthe & Elixir. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-elixir/4-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with Absinthe, implement query resolvers in Elixir and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-yoga, Node.js & Prisma and best practices for authentication, filtering, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Node.js & Express as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-js/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Query",
							"description": "Learn how to define a GraphQL schema, implement query resolvers with Node.js and test your queries in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/2-a-simple-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Adding a Database",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/graphql-js/4-adding-a-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Mutation",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-js, Node.js & Prisma. Test your implementation in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/3-a-simple-mutation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting Server and Database with the Prisma Client",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/graphql-js/5-connecting-server-and-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime GraphQL Subscriptions",
							"description": "Learn how to implement GraphQL subscriptions with Node.js, Express & Prisma to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-js/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing authentication and authorization with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/6-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering, Pagination & Sorting",
							"description": "Learn how to add filtering and pagination capabilities to a GraphQL API with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/8-filtering-pagination-and-sorting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Node.js and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Building a Server with GraphQL and Graphene"
						},
						"fields": {
							"slug": "/graphql-python/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Setting up a Django Graphene Project"
						},
						"fields": {
							"slug": "/graphql-python/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Tutorial Summary"
						},
						"fields": {
							"slug": "/graphql-python/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Creating Data With Mutations"
						},
						"fields": {
							"slug": "/graphql-python/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Creating Your First Queries"
						},
						"fields": {
							"slug": "/graphql-python/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Authenticating on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Links and Voting",
							"description": "Enable Users to create Links and to Vote on them"
						},
						"fields": {
							"slug": "/graphql-python/5-links-and-voting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Handling Errors on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Filtering GraphQL Queries"
						},
						"fields": {
							"slug": "/graphql-python/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Simple Pagination with Graphene"
						},
						"fields": {
							"slug": "/graphql-python/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relay",
							"description": "Using Relay on Graphene"
						},
						"fields": {
							"slug": "/graphql-python/9-relay/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-java/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-java and best practices for defining the GraphQL schema in this tutorial."
						},
						"fields": {
							"slug": "/graphql-java/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with a Node.js & Express GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/10-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Alternative approaches to schema development",
							"description": "Learn about an alternative approach to developing schemas with graphl-java with graphql-spqr"
						},
						"fields": {
							"slug": "/graphql-java/11-alternative-approaches/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-java/12-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-java, implement query resolvers in Java and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-java & Java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connectors",
							"description": "Learn how to write connectors in Java to load data from MongoDB with graphql-java and a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/4-connectors/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Java."
						},
						"fields": {
							"slug": "/graphql-java/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Java and graphql-java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/6-more-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Subscriptions",
							"description": "Learn how to implement server-side GraphQL subscriptions with Java to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-java/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Java, Node.js & Express."
						},
						"fields": {
							"slug": "/graphql-java/7-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/9-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-ruby and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-ruby/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-ruby and best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-ruby/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-ruby, implement query resolvers in Ruby and use a GraphiQL Playground to test your queries."
						},
						"fields": {
							"slug": "/graphql-ruby/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting Nodes",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/5-connecting-nodes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/graphql-scala/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "In this chapter will be described how to setup the HTTP server, install all dependencies and setup the database."
						},
						"fields": {
							"slug": "/graphql-scala/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-ruby & Ruby and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-ruby/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "The first query",
							"description": "In this chapter you will learn last steps of preparation, add graphiql console for easy debugging and at the end, run your first query."
						},
						"fields": {
							"slug": "/graphql-scala/2-preparing-first-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Handling Arguments",
							"description": "In this chapter you will learn how to use arguments and how to handle them and pass to the business logic."
						},
						"fields": {
							"slug": "/graphql-scala/3-handling_arguments/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Custom Scalars",
							"description": "In this chapter Link model will get an additional field to store date and time. You will learn how to handle such custom types and use scalars."
						},
						"fields": {
							"slug": "/graphql-scala/5-custom_scalars/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deferred Resolvers",
							"description": "In this chapter you will learn how to improve performance with deferred resolvers."
						},
						"fields": {
							"slug": "/graphql-scala/4-deferred_resolvers/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Interfaces",
							"description": "In this chapter we will introduce two additional models. We show the most common parts and extract those as an interface. This time you will work alone."
						},
						"fields": {
							"slug": "/graphql-scala/6-interfaces/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relations",
							"description": "In this chapter you will learn how to connect models with relations. You will also learn how to prepare fetchers to use either relations or ids to get entities."
						},
						"fields": {
							"slug": "/graphql-scala/7-relations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "In this chapter you will learn how to add data to the database."
						},
						"fields": {
							"slug": "/graphql-scala/8-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Sangria's Middleware allows pre-processing queries. Gather all useful data in the following subqueries."
						},
						"fields": {
							"slug": "/graphql-scala/9-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "adding new Snap settings to code",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/0-adding-new-Snap-settings-to-code/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "adding users",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/1-adding-users/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "custom css themes",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/10-custom-css-themes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "apache",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/2-apache/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "archive and delete",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/3-archive-and-delete/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "aws",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/4-aws/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "azure",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/5-azure/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "backup",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/6-backup/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "caddy webserver config",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/7-caddy-webserver-config/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "cloud foundry",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/8-cloud-foundry/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "cloudron",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/9-cloudron/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Angular & Apollo Client. You'll use angular-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/angular-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with angular-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with Angular and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/angular-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading link-items",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Angular & Apollo Client you learned how to build a production-ready Hackernews clone using angular-cli & Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's service injected in the component to define and send mutations."
						},
						"fields": {
							"slug": "/angular-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use Angular Router  together with GraphQL and Apollo Client to implement navigation in an Angular app."
						},
						"fields": {
							"slug": "/angular-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Angular app with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/angular-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & Angular. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/angular-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, React & Apollo Client. Use create-react-app for the frontend and graphql-yoga & Prisma for the backend."
						},
						"fields": {
							"slug": "/react-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a Angular app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/angular-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & Apollo Client you learned how to build a production-ready Hackernews clone using create-react-app & Prisma."
						},
						"fields": {
							"slug": "/react-apollo/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's `<Mutation />` component to define and send mutations."
						},
						"fields": {
							"slug": "/react-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use react-router 4 together with GraphQL and Apollo Client to implement navigation in a React app. Each route will be represented as a `Link`."
						},
						"fields": {
							"slug": "/react-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in a React app with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your React components."
						},
						"fields": {
							"slug": "/react-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Prisma provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/react-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a React app. The Prisma API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/react-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a production-ready Hackernews clone with GraphQL, React & Relay. You'll use create-react-app for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/react-relay/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL Fragments and Relay's QueryRenderer to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-relay/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how to use Relay Modern's new Mutation API to send mutations to a GraphQL server. With Relay Modern you can also update the cache directly after the mutation."
						},
						"fields": {
							"slug": "/react-relay/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": null
						},
						"fields": {
							"slug": "/react-relay/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Relay to provide an email-and-password-based login in a React app with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Relay Modern & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-relay/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Relay Modern's imperative API to update the cache after a GraphQL mutation. The updates will automatically end up in your React components."
						},
						"fields": {
							"slug": "/react-relay/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement cursor-based pagination with GraphQL and Relay Modern using `PaginationContainer`. Relay uses the concept of connections to represent lists."
						},
						"fields": {
							"slug": "/react-relay/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & Relay Modern you learned how to build a production-ready Hackernews clone using create-react-app & Graphcool."
						},
						"fields": {
							"slug": "/react-relay/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Clients",
							"description": "Learn about GraphQL clients, like Relay and Apollo Client, and the features they provide, such as caching, schema validation and query/view colocation."
						},
						"fields": {
							"slug": "/advanced/0-clients/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More GraphQL Concepts",
							"description": "Learn about advanced concepts of the GraphQL language like fragments, query arguments, aliases, interfaces and more SDL features"
						},
						"fields": {
							"slug": "/advanced/2-more-graphql-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Server",
							"description": "Learn about the major concepts of GraphQL servers, how it executes GraphQL queries with resolver functions and batches multiple requests."
						},
						"fields": {
							"slug": "/advanced/1-server/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Tooling and Ecosystem",
							"description": "Learn about the GraphQL ecosystem and its awesome tooling like the GraphiQL playgrounds or self-generating docs through introspection."
						},
						"fields": {
							"slug": "/advanced/3-tooling-and-ecosystem/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Security",
							"description": "Learn about different security aspects and strategies for GraphQL, such as timeouts, maximum query depth, query complexity and throttling."
						},
						"fields": {
							"slug": "/advanced/4-security/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Common Questions",
							"description": "Answer common GraphQL questions about server-side caching, authentication, authorization, error handling, offline usage and more."
						},
						"fields": {
							"slug": "/advanced/5-common-questions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, VueJS & Apollo Client. You'll use vue-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/vue-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with VueJS & Apollo Client you learned how to build a production-ready Hackernews clone using vue-cli & Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's `mutate` method to define and send mutations."
						},
						"fields": {
							"slug": "/vue-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use vue-router together with GraphQL and Apollo Client to implement navigation in a VueJS app."
						},
						"fields": {
							"slug": "/vue-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with vue-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with VueJS and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/vue-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in a VueJS app with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/vue-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & VueJS. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/vue-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn all about GraphQL and why it is an API technology that's superior to REST. It is not only for React & Javascript developers but can be used for any API."
						},
						"fields": {
							"slug": "/basics/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "GraphQL is the better REST",
							"description": "Learn why GraphQL is a more efficient and flexible alternative to REST APIs. It has a strong type system and avoids frontend issues like over- and underfetching."
						},
						"fields": {
							"slug": "/basics/1-graphql-is-the-better-rest/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Core Concepts",
							"description": "Learn about the basic GraphQL language concepts, such as Queries, Mutations, Subscriptions and the GraphQL Schema & SDL. Try them out in your own sandbox."
						},
						"fields": {
							"slug": "/basics/2-core-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Ember, and Apollo Client. You'll use Ember and ember-apollo-client for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/ember-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Big Picture (Architecture)",
							"description": "Learn about different architectural use cases of GraphQL and the major components on the backend and the frontend, like resolver functions and client libraries."
						},
						"fields": {
							"slug": "/basics/3-big-picture/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with ember-cli, ember-apollo-client, and the backend with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/ember-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Navigation",
							"description": "Learn how to use Ember components together with GraphQL and Apollo Client to implement navigation in an Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/4-navigation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use `ember-apollo-client` to send mutations."
						},
						"fields": {
							"slug": "/ember-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/ember-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Ember & Apollo Client you learned how to build a production-ready Hackernews clone using ember-apollo-client & Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/8-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a VueJS app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/vue-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Ember app with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/5-authentication/"
						}
					}
				}
			]
		}
	},
	"pathContext": {
		"slug": "/graphql-ruby/4-authentication/"
	}
};


//////////////////
// WEBPACK FOOTER
// ./~/json-loader!./.cache/json/graphql-ruby-4-authentication.json
// module id = ./node_modules/json-loader/index.js!./.cache/json/graphql-ruby-4-authentication.json
// module chunks = 75